/* eslint-disable react-refresh/only-export-components */
import * as React from 'react';
import { createPortal } from 'react-dom';
import { parseDate, Time, type CalendarDate } from '@internationalized/date';
import { cn } from '@/lib/cn';
import { StandardIcon } from '@/components/common/icon';

type Color = 'default' | 'primary' | 'danger' | 'success' | 'warning';
type Variant = 'solid' | 'flat' | 'light' | 'ghost';
type Size = 'sm' | 'md' | 'lg';
type Key = React.Key;

export type SharedSelection = Set<Key> & { currentKey?: string | null };
export type Pagination = {
  page?: number;
  total?: number;
};

function cleanKey(key: Key | null | undefined, fallback: string): string {
  if (key === null || typeof key === 'undefined') {
    return fallback;
  }

  return String(key).replace(/^\.\$?/, '');
}

function makeSelection(keys: Key[]): SharedSelection {
  const set = new Set(keys) as SharedSelection;
  const first = keys.length ? String(keys[0]) : null;
  set.currentKey = first;

  return set;
}

function toKeyList(value: unknown): Key[] {
  if (!value) {
    return [];
  }

  if (value instanceof Set) {
    return Array.from(value);
  }

  if (Array.isArray(value)) {
    return value as Key[];
  }

  return [value as Key];
}

function colorClass(color: Color, variant: Variant): string {
  if (variant === 'light') {
    if (color === 'danger') {
      return 'bg-transparent text-red-600 hover:bg-red-50';
    }

    if (color === 'success') {
      return 'bg-transparent text-emerald-700 hover:bg-emerald-50';
    }

    if (color === 'warning') {
      return 'bg-transparent text-amber-700 hover:bg-amber-50';
    }

    if (color === 'primary') {
      return 'bg-transparent text-blue-700 hover:bg-blue-50';
    }

    return 'bg-transparent text-slate-700 hover:bg-slate-100';
  }

  if (variant === 'ghost') {
    if (color === 'danger') {
      return 'border border-red-200 bg-white text-red-700 hover:bg-red-50';
    }

    if (color === 'primary') {
      return 'border border-blue-200 bg-white text-blue-700 hover:bg-blue-50';
    }

    return 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50';
  }

  if (variant === 'flat') {
    if (color === 'danger') {
      return 'bg-red-100 text-red-700 hover:bg-red-200';
    }

    if (color === 'success') {
      return 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200';
    }

    if (color === 'warning') {
      return 'bg-amber-100 text-amber-800 hover:bg-amber-200';
    }

    if (color === 'primary') {
      return 'bg-blue-100 text-blue-700 hover:bg-blue-200';
    }

    return 'bg-slate-100 text-slate-700 hover:bg-slate-200';
  }

  if (color === 'danger') {
    return 'bg-red-600 text-white hover:bg-red-700';
  }

  if (color === 'success') {
    return 'bg-emerald-600 text-white hover:bg-emerald-700';
  }

  if (color === 'warning') {
    return 'bg-amber-500 text-white hover:bg-amber-600';
  }

  if (color === 'primary') {
    return 'bg-blue-600 text-white hover:bg-blue-700';
  }

  return 'bg-slate-900 text-white hover:bg-slate-800';
}

function sizeClass(size: Size): string {
  if (size === 'sm') {
    return 'h-8 px-3 text-sm';
  }

  if (size === 'lg') {
    return 'h-11 px-5 text-base';
  }

  return 'h-9 px-4 text-sm';
}

export function HeroUIProvider({
  children,
}: {
  children: React.ReactNode;
  navigate?: (_path: string, _options?: unknown) => void;
  useHref?: (_to: string) => string;
}) {
  return <>{children}</>;
}

export function useDisclosure(options?: {
  defaultOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}) {
  const [isOpen, setOpen] = React.useState(Boolean(options?.defaultOpen));

  const onOpen = React.useCallback(() => {
    setOpen(true);
    options?.onOpen?.();
  }, [options]);

  const onClose = React.useCallback(() => {
    setOpen(false);
    options?.onClose?.();
  }, [options]);

  const onOpenChange = React.useCallback(
    (value?: boolean) => {
      const next = typeof value === 'boolean' ? value : !isOpen;
      setOpen(next);
      if (next) {
        options?.onOpen?.();
      } else {
        options?.onClose?.();
      }
    },
    [isOpen, options],
  );

  const onToggle = React.useCallback(() => onOpenChange(), [onOpenChange]);

  return { isOpen, onOpen, onClose, onOpenChange, onToggle };
}

export function Button({
  children,
  className,
  variant = 'solid',
  color = 'default',
  size = 'md',
  isIconOnly,
  isLoading,
  isDisabled,
  startContent,
  endContent,
  onPress,
  disabled,
  onClick,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  color?: Color;
  size?: Size;
  isIconOnly?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  onPress?: () => void;
}) {
  const blocked = Boolean(disabled || isDisabled || isLoading);

  return (
    <button
      type='button'
      {...props}
      disabled={blocked}
      onClick={(e) => {
        onClick?.(e);
        if (!blocked) {
          onPress?.();
        }
      }}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-md font-medium transition',
        'disabled:cursor-not-allowed disabled:opacity-50',
        sizeClass(size),
        colorClass(color, variant),
        isIconOnly ? 'h-9 w-9 p-0' : '',
        className,
      )}
    >
      {isLoading ? (
        <span className='h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent' />
      ) : (
        startContent
      )}
      {children}
      {!isLoading && endContent}
    </button>
  );
}

export function ButtonGroup({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={cn('inline-flex items-center gap-1', className)}>
      {children}
    </div>
  );
}

export function Card({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  shadow?: 'none' | 'sm' | 'md' | 'lg';
}) {
  return (
    <div
      {...props}
      className={cn(
        'rounded-xl border border-slate-200 bg-white text-slate-900 shadow-sm',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...props}
      className={cn('border-b border-slate-100 px-4 py-3', className)}
    >
      {children}
    </div>
  );
}

export function CardBody({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...props} className={cn('px-4 py-3', className)}>
      {children}
    </div>
  );
}

export function Badge({
  children,
  content,
  className,
  color = 'primary',
}: {
  children?: React.ReactNode;
  content?: React.ReactNode;
  className?: string;
  isOneChar?: boolean;
  placement?: 'bottom-right' | 'top-right' | 'top-left' | 'bottom-left';
  size?: 'sm' | 'md' | 'lg';
  color?: Color;
}) {
  const colorMap: Record<Color, string> = {
    default: 'bg-slate-800 text-white',
    primary: 'bg-blue-600 text-white',
    danger: 'bg-red-600 text-white',
    success: 'bg-emerald-600 text-white',
    warning: 'bg-amber-500 text-white',
  };

  if (!children) {
    return (
      <span
        className={cn(
          'inline-flex rounded-full px-2 py-0.5 text-xs',
          colorMap[color],
          className,
        )}
      >
        {content}
      </span>
    );
  }

  return (
    <span className={cn('relative inline-flex', className)}>
      {children}
      <span
        className={cn(
          'absolute -right-2 -top-2 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-semibold',
          colorMap[color],
        )}
      >
        {content}
      </span>
    </span>
  );
}

export function Chip({
  className,
  children,
  isCloseable,
  onClose,
  style,
}: {
  className?: string;
  children?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  radius?: 'full' | 'sm' | 'md' | 'lg';
  variant?: 'flat' | 'solid' | 'bordered';
  isCloseable?: boolean;
  onClose?: () => void;
  style?: React.CSSProperties;
}) {
  return (
    <span
      style={style}
      className={cn(
        'inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700',
        className,
      )}
    >
      <span>{children}</span>
      {isCloseable ? (
        <button
          type='button'
          aria-label='Remove'
          className='rounded p-0.5 hover:bg-black/10'
          onClick={onClose}
        >
          x
        </button>
      ) : null}
    </span>
  );
}

export function Avatar({
  className,
  name,
  src,
  size = 'md',
  color,
  showFallback,
  getInitials,
  fallback,
  as,
  style,
  onClick,
}: {
  className?: string;
  name?: string;
  src?: string;
  size?: 'sm' | 'md' | 'lg';
  color?: Color;
  showFallback?: boolean;
  getInitials?: () => string;
  fallback?: React.ReactNode;
  as?: 'button' | 'div';
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLElement>;
  alt?: string;
}) {
  const [imgError, setImgError] = React.useState(false);
  React.useEffect(() => {
    setImgError(false);
  }, [src]);

  const initials =
    getInitials?.() ||
    (name
      ? name
          .trim()
          .split(/\s+/)
          .map((v) => v[0])
          .slice(0, 2)
          .join('')
          .toUpperCase()
      : '');
  const sizeClassMap = {
    sm: 'h-8 w-8 text-xs',
    md: 'h-10 w-10 text-sm',
    lg: 'h-12 w-12 text-base',
  };
  const colorClass =
    color === 'primary'
      ? 'bg-primary-500 text-white'
      : 'bg-slate-200 text-slate-700';
  const rootClass = cn(
    'inline-flex items-center justify-center overflow-hidden rounded-full',
    colorClass,
    sizeClassMap[size],
    className,
  );
  const body =
    src && !imgError ? (
      <img
        src={src}
        alt={name || 'avatar'}
        className='h-full w-full object-cover'
        onError={() => setImgError(true)}
      />
    ) : (
      <>
        {fallback ? fallback : null}
        {!fallback && (showFallback || initials) ? (
          <span>{initials}</span>
        ) : null}
      </>
    );

  if (as === 'button') {
    return (
      <button
        type='button'
        style={style}
        className={rootClass}
        onClick={
          onClick as React.MouseEventHandler<HTMLButtonElement> | undefined
        }
      >
        {body}
      </button>
    );
  }

  return (
    <div
      style={style}
      className={rootClass}
      onClick={onClick as React.MouseEventHandler<HTMLDivElement> | undefined}
    >
      {body}
    </div>
  );
}

export function Divider({
  className,
  orientation = 'horizontal',
}: {
  className?: string;
  orientation?: 'horizontal' | 'vertical';
}) {
  if (orientation === 'vertical') {
    return <div className={cn('h-full w-px bg-slate-200', className)} />;
  }

  return <div className={cn('h-px w-full bg-slate-200', className)} />;
}

export function Spacer({ className }: { className?: string }) {
  return <div className={cn('block', className)} />;
}

export function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...props}
      className={cn('animate-pulse rounded-md bg-slate-200', className)}
    />
  );
}

export function Progress({
  className,
  value = 0,
  isIndeterminate,
}: {
  className?: string;
  value?: number;
  isIndeterminate?: boolean;
  size?: 'sm' | 'md' | 'lg';
  'aria-label'?: string;
}) {
  return (
    <div
      className={cn(
        'h-2 w-full overflow-hidden rounded-full bg-slate-200',
        className,
      )}
    >
      <div
        className={cn(
          'h-full bg-blue-600 transition-all',
          isIndeterminate ? 'w-1/3 animate-pulse' : '',
        )}
        style={
          isIndeterminate
            ? undefined
            : { width: `${Math.max(0, Math.min(100, value))}%` }
        }
      />
    </div>
  );
}

export function CircularProgress({
  className,
  size = 'md',
  isIndeterminate,
}: {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  isIndeterminate?: boolean;
  'aria-label'?: string;
}) {
  let sizeClass = 'h-6 w-6';
  if (size === 'lg') {
    sizeClass = 'h-8 w-8';
  } else if (size === 'sm') {
    sizeClass = 'h-4 w-4';
  }

  return (
    <span
      className={cn(
        'inline-block rounded-full border-2 border-slate-300 border-r-blue-600',
        isIndeterminate !== false ? 'animate-spin' : '',
        sizeClass,
        className,
      )}
    />
  );
}

export function Spinner({
  className,
  size = 'md',
}: {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  return <CircularProgress className={className} size={size} isIndeterminate />;
}

export function Alert({
  className,
  title,
  children,
  color = 'default',
  variant = 'flat',
}: {
  className?: string;
  title?: React.ReactNode;
  children?: React.ReactNode;
  color?: Color;
  variant?: Variant;
}) {
  const colorMap: Record<Color, string> = {
    default: 'border-slate-200 bg-slate-50 text-slate-700',
    primary: 'border-blue-200 bg-blue-50 text-blue-700',
    danger: 'border-red-200 bg-red-50 text-red-700',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    warning: 'border-amber-200 bg-amber-50 text-amber-800',
  };

  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2 text-sm',
        colorMap[color],
        variant === 'solid' ? 'text-white' : '',
        className,
      )}
    >
      {title ? <div className='font-medium'>{title}</div> : null}
      {children ? <div>{children}</div> : null}
    </div>
  );
}

export function Form({
  className,
  children,
  ...props
}: React.FormHTMLAttributes<HTMLFormElement>) {
  return (
    <form {...props} className={cn(className)}>
      {children}
    </form>
  );
}

type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  label?: React.ReactNode;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  isClearable?: boolean;
  onValueChange?: (value: string) => void;
  onClear?: () => void;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  wrapperClassName?: string;
};

export function Input({
  className,
  wrapperClassName,
  label,
  startContent,
  endContent,
  isClearable,
  onValueChange,
  onClear,
  value,
  defaultValue,
  isReadOnly,
  readOnly,
  isDisabled,
  disabled,
  onChange,
  ...props
}: InputProps) {
  const [internal, setInternal] = React.useState(String(defaultValue ?? ''));
  const controlled = typeof value !== 'undefined';
  const current = controlled ? String(value ?? '') : internal;
  const disabledValue = Boolean(disabled || isDisabled);
  const readOnlyValue = Boolean(readOnly || isReadOnly);

  const setValue = (next: string) => {
    if (!controlled) {
      setInternal(next);
    }

    onValueChange?.(next);
  };

  return (
    <label className={cn('flex w-full flex-col gap-1 text-sm', className)}>
      {label ? (
        <span className='text-xs font-medium text-slate-600'>{label}</span>
      ) : null}
      <span
        className={cn(
          'flex min-h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-3',
          wrapperClassName,
        )}
      >
        {startContent}
        <input
          {...props}
          className='w-full bg-transparent text-sm outline-none'
          value={current}
          readOnly={readOnlyValue}
          disabled={disabledValue}
          onChange={(e) => {
            onChange?.(e);
            setValue(e.target.value);
          }}
        />
        {isClearable && current ? (
          <button
            type='button'
            className='rounded p-0.5 text-slate-500 hover:bg-slate-100'
            onClick={() => {
              setValue('');
              onClear?.();
            }}
            aria-label='Clear'
          >
            <StandardIcon id='mdi:close' size={16} className='text-slate-500' />
          </button>
        ) : (
          endContent
        )}
      </span>
    </label>
  );
}

export function Textarea({
  className,
  label,
  value,
  defaultValue,
  onChange,
  onValueChange,
  minRows,
  maxRows,
  ...props
}: Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> & {
  label?: React.ReactNode;
  onValueChange?: (value: string) => void;
  minRows?: number;
  maxRows?: number;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
}) {
  const [internal, setInternal] = React.useState(String(defaultValue ?? ''));
  const controlled = typeof value !== 'undefined';
  const current = controlled ? String(value ?? '') : internal;

  return (
    <label className={cn('flex w-full flex-col gap-1 text-sm', className)}>
      {label ? (
        <span className='text-xs font-medium text-slate-600'>{label}</span>
      ) : null}
      <textarea
        {...props}
        rows={minRows || 3}
        className='w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none'
        value={current}
        onChange={(e) => {
          onChange?.(e);
          if (!controlled) {
            setInternal(e.target.value);
          }

          onValueChange?.(e.target.value);
        }}
      />
      {maxRows ? <span className='hidden'>{maxRows}</span> : null}
    </label>
  );
}

type SelectItemDescriptor = {
  key: string;
  textValue: string;
  content: React.ReactNode;
  startContent?: React.ReactNode;
  rawData?: unknown;
};

type SelectItemProps = {
  children?: React.ReactNode;
  textValue?: string;
  startContent?: React.ReactNode;
};

export function SelectItem(_props: SelectItemProps) {
  return null;
}

function normalizeSelectItems<T = unknown>(params: {
  items?: T[];
  children?: React.ReactNode | ((item: T) => React.ReactNode);
}): SelectItemDescriptor[] {
  const result: SelectItemDescriptor[] = [];
  const { items, children } = params;

  if (Array.isArray(items) && typeof children === 'function') {
    items.forEach((item, index) => {
      const rendered = children(item);
      const arr = React.Children.toArray(rendered);
      arr.forEach((node, subIndex) => {
        if (!React.isValidElement<SelectItemProps>(node)) {
          return;
        }

        const key = cleanKey(node.key, `${index}-${subIndex}`);
        const textValue =
          node.props.textValue ||
          (typeof node.props.children === 'string'
            ? node.props.children
            : String(key));
        result.push({
          key,
          textValue,
          content: node.props.children,
          startContent: node.props.startContent,
          rawData: item,
        });
      });
    });

    return result;
  }

  if (typeof children === 'function') {
    return result;
  }

  const arr = React.Children.toArray(children);
  arr.forEach((node, index) => {
    if (!React.isValidElement<SelectItemProps>(node)) {
      return;
    }

    const key = cleanKey(node.key, `${index}`);
    const textValue =
      node.props.textValue ||
      (typeof node.props.children === 'string'
        ? node.props.children
        : String(key));
    result.push({
      key,
      textValue,
      content: node.props.children,
      startContent: node.props.startContent,
    });
  });

  return result;
}

export function Select<T = unknown>({
  className,
  label,
  items,
  children,
  selectedKeys,
  defaultSelectedKeys,
  onSelectionChange,
  onChange,
  selectionMode = 'single',
  placeholder,
  isDisabled,
  isReadOnly,
  startContent,
  renderValue,
}: {
  className?: string;
  label?: React.ReactNode;
  items?: T[];
  children?: React.ReactNode | ((item: T) => React.ReactNode);
  selectedKeys?: Iterable<Key>;
  defaultSelectedKeys?: Iterable<Key>;
  onSelectionChange?: (keys: SharedSelection) => void;
  onChange?: React.ChangeEventHandler<HTMLSelectElement>;
  selectionMode?: 'single' | 'multiple';
  placeholder?: string;
  variant?: Variant;
  size?: Size;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  startContent?: React.ReactNode;
  'aria-label'?: string;
  renderValue?: (
    items: Array<{ key: string; textValue: string; data?: unknown }>,
  ) => React.ReactNode;
}) {
  const descriptors = normalizeSelectItems<T>({ items, children });
  const controlled = typeof selectedKeys !== 'undefined';
  const [internal, setInternal] = React.useState<Key[]>(
    toKeyList(defaultSelectedKeys),
  );
  const currentKeys = controlled ? toKeyList(selectedKeys) : internal;
  const currentSet = new Set(currentKeys.map((k) => String(k)));

  const selectedDescriptors = descriptors.filter((item) =>
    currentSet.has(item.key),
  );

  const renderedValue = renderValue
    ? renderValue(
        selectedDescriptors.map((item) => ({
          key: item.key,
          textValue: item.textValue,
          data: item.rawData,
        })),
      )
    : null;

  return (
    <label className={cn('flex w-full flex-col gap-1 text-sm', className)}>
      {label ? (
        <span className='text-xs font-medium text-slate-600'>{label}</span>
      ) : null}
      {renderedValue ? (
        <div className='mb-1 text-sm text-slate-700'>{renderedValue}</div>
      ) : null}
      <span className='flex min-h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-3'>
        {startContent}
        <select
          className='w-full bg-transparent text-sm outline-none'
          multiple={selectionMode === 'multiple'}
          disabled={isDisabled || isReadOnly}
          value={
            selectionMode === 'multiple'
              ? currentKeys.map((k) => String(k))
              : String(currentKeys[0] || '')
          }
          onChange={(e) => {
            onChange?.(e);
            let nextKeys: string[] = [];
            if (selectionMode === 'multiple') {
              nextKeys = Array.from(e.target.selectedOptions).map(
                (option) => option.value,
              );
            } else if (e.target.value) {
              nextKeys = [e.target.value];
            }

            if (!controlled) {
              setInternal(nextKeys);
            }

            onSelectionChange?.(makeSelection(nextKeys));
          }}
        >
          {selectionMode === 'single' ? (
            <option value=''>{placeholder || 'Select an option'}</option>
          ) : null}
          {descriptors.map((item) => (
            <option key={item.key} value={item.key}>
              {item.textValue}
            </option>
          ))}
        </select>
      </span>
    </label>
  );
}

type AutocompleteItemProps = {
  children?: React.ReactNode;
  textValue?: string;
};

export function AutocompleteItem(_props: AutocompleteItemProps) {
  return null;
}

function normalizeAutocompleteItems<T = unknown>(params: {
  items?: T[];
  children?: React.ReactNode | ((item: T) => React.ReactNode);
}): Array<{ key: string; textValue: string; content: React.ReactNode }> {
  const result: Array<{
    key: string;
    textValue: string;
    content: React.ReactNode;
  }> = [];
  const { items, children } = params;

  if (Array.isArray(items) && typeof children === 'function') {
    items.forEach((item, index) => {
      const rendered = children(item);
      const arr = React.Children.toArray(rendered);
      arr.forEach((node, subIndex) => {
        if (!React.isValidElement<AutocompleteItemProps>(node)) {
          return;
        }

        const key = cleanKey(node.key, `${index}-${subIndex}`);
        const textValue =
          node.props.textValue ||
          (typeof node.props.children === 'string'
            ? node.props.children
            : String(key));
        result.push({
          key,
          textValue,
          content: node.props.children,
        });
      });
    });

    return result;
  }

  if (typeof children === 'function') {
    return result;
  }

  const arr = React.Children.toArray(children);
  arr.forEach((node, index) => {
    if (!React.isValidElement<AutocompleteItemProps>(node)) {
      return;
    }

    const key = cleanKey(node.key, `${index}`);
    const textValue =
      node.props.textValue ||
      (typeof node.props.children === 'string'
        ? node.props.children
        : String(key));
    result.push({
      key,
      textValue,
      content: node.props.children,
    });
  });

  return result;
}

export function Autocomplete<T = unknown>({
  className,
  label,
  placeholder,
  items,
  defaultItems,
  children,
  inputValue,
  onInputChange,
  onSelectionChange,
  selectedKey,
  isLoading,
  listboxProps,
  disabled,
  readOnly,
  onChange,
}: {
  className?: string;
  label?: React.ReactNode;
  placeholder?: string;
  items?: T[];
  defaultItems?: T[];
  children?: React.ReactNode | ((item: T) => React.ReactNode);
  inputValue?: string;
  onInputChange?: (value: string) => void;
  onSelectionChange?: (key: Key | null) => void;
  selectedKey?: Key | null;
  isLoading?: boolean;
  listboxProps?: { emptyContent?: React.ReactNode; portal?: boolean };
  selectedKeys?: Iterable<Key>;
  menuTrigger?: 'focus' | 'input' | 'manual';
  variant?: Variant;
  'aria-label'?: string;
  disabled?: boolean;
  readOnly?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}) {
  const sourceItems = (items || defaultItems || []) as T[];
  const descriptors = normalizeAutocompleteItems<T>({
    items: sourceItems,
    children,
  });
  const [internalValue, setInternalValue] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [listboxRect, setListboxRect] = React.useState<DOMRect | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    if (open && listboxProps?.portal && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setListboxRect(rect);
    } else {
      setListboxRect(null);
    }
  }, [open, listboxProps?.portal]);

  const controlled = typeof inputValue !== 'undefined';
  const query = controlled ? inputValue || '' : internalValue;
  const selectedValue =
    selectedKey === null || typeof selectedKey === 'undefined'
      ? ''
      : String(selectedKey);

  const filtered = descriptors.filter((item) =>
    item.textValue.toLowerCase().includes(query.toLowerCase()),
  );

  const updateQuery = (next: string) => {
    if (!controlled) {
      setInternalValue(next);
    }

    onInputChange?.(next);
  };

  const listboxContent = open ? (
    <>
      {isLoading ? (
        <div className='px-2 py-1 text-sm text-slate-500'>Loading...</div>
      ) : null}
      {!isLoading && filtered.length === 0 ? (
        <div className='px-2 py-1 text-sm text-slate-500'>
          {listboxProps?.emptyContent || 'No results'}
        </div>
      ) : null}
      {!isLoading
        ? filtered.map((item) => {
            const active = item.key === selectedValue;
            return (
              <button
                key={item.key}
                type='button'
                className={cn(
                  'flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-100',
                  active ? 'bg-slate-100' : '',
                )}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelectionChange?.(item.key);
                  updateQuery(item.textValue);
                  setOpen(false);
                }}
              >
                {item.content}
              </button>
            );
          })
        : null}
    </>
  ) : null;

  const usePortal = Boolean(listboxProps?.portal && listboxRect);
  const listboxEl = (
    <div
      className='z-[9999] max-h-64 overflow-auto rounded-md border border-slate-200 bg-white p-1 shadow-lg'
      style={
        usePortal && listboxRect
          ? {
              position: 'fixed',
              top: listboxRect.bottom + 4,
              left: listboxRect.left,
              width: listboxRect.width,
              minWidth: 'min(16rem, 100vw)',
            }
          : undefined
      }
    >
      {listboxContent}
    </div>
  );

  return (
    <div ref={wrapperRef} className={cn('relative w-full', className)}>
      <Input
        label={label}
        placeholder={placeholder}
        value={query}
        isDisabled={disabled}
        isReadOnly={readOnly}
        onValueChange={updateQuery}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          window.setTimeout(() => setOpen(false), 120);
        }}
        onChange={onChange}
      />
      {open && !usePortal ? (
        <div className='absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-md border border-slate-200 bg-white p-1 shadow-lg'>
          {listboxContent}
        </div>
      ) : null}
      {usePortal && listboxContent
        ? createPortal(listboxEl, document.body)
        : null}
    </div>
  );
}

type TableContextValue = {
  columnKeys: string[];
  isHeaderSticky?: boolean;
};

const TableContext = React.createContext<TableContextValue>({
  columnKeys: [],
  isHeaderSticky: false,
});

export function Table({
  children,
  className,
  bottomContent,
  isHeaderSticky,
}: React.HTMLAttributes<HTMLDivElement> & {
  children?: React.ReactNode;
  bottomContent?: React.ReactNode;
  isHeaderSticky?: boolean;
}) {
  const childArray = React.Children.toArray(children);
  const headerElement = childArray.find(
    (node) => React.isValidElement(node) && node.type === TableHeader,
  ) as React.ReactElement<{ columns?: Array<{ key: Key }> }> | undefined;
  const columnKeys = (headerElement?.props.columns || []).map((col) =>
    String(col.key),
  );

  return (
    <TableContext.Provider value={{ columnKeys, isHeaderSticky }}>
      <div
        className={cn(
          'flex max-h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white',
          className,
        )}
      >
        <div className='min-h-0 flex-1 overflow-auto'>
          <table className='min-w-full divide-y divide-slate-200 text-sm'>
            {children}
          </table>
        </div>
        {bottomContent ? (
          <div className='flex-shrink-0 border-t border-slate-100 p-2'>
            {bottomContent}
          </div>
        ) : null}
      </div>
    </TableContext.Provider>
  );
}

export function TableHeader<T>({
  columns,
  children,
}: {
  columns?: T[];
  children?: React.ReactNode | ((col: T) => React.ReactNode);
}) {
  const { isHeaderSticky } = React.useContext(TableContext);
  let content: React.ReactNode;
  if (columns && typeof children === 'function') {
    content = columns.map((col, index) => (
      <React.Fragment key={index}>{children(col)}</React.Fragment>
    ));
  } else if (typeof children === 'function') {
    content = null;
  } else {
    content = children;
  }

  return (
    <thead
      className={cn(
        'bg-slate-50',
        isHeaderSticky &&
          'sticky top-0 z-10 border-b border-slate-200 shadow-[0_1px_0_0_rgba(0,0,0,0.05)]',
      )}
    >
      <tr>{content}</tr>
    </thead>
  );
}

export function TableColumn({
  className,
  children,
}: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn(
        'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function TableBody<T>({
  items,
  children,
  isLoading,
  emptyContent,
}: {
  items?: T[];
  children?: React.ReactNode | ((item: T) => React.ReactNode);
  isLoading?: boolean;
  emptyContent?: React.ReactNode;
}) {
  const { columnKeys } = React.useContext(TableContext);
  const spanCount = columnKeys.length || 1;

  if (isLoading) {
    return (
      <tbody>
        <tr>
          <td
            className='px-3 py-6 text-center text-slate-500'
            colSpan={spanCount}
          >
            Loading...
          </td>
        </tr>
      </tbody>
    );
  }

  if (!items?.length) {
    return (
      <tbody>
        <tr>
          <td
            className='px-3 py-6 text-center text-slate-500'
            colSpan={spanCount}
          >
            {emptyContent || 'No data'}
          </td>
        </tr>
      </tbody>
    );
  }

  const rows =
    typeof children === 'function'
      ? items.map((item, index) => (
          <React.Fragment key={index}>{children(item)}</React.Fragment>
        ))
      : children;

  return <tbody className='divide-y divide-slate-100'>{rows}</tbody>;
}

export function TableRow({
  children,
}: {
  children?: React.ReactNode | ((columnKey: Key) => React.ReactNode);
}) {
  const { columnKeys } = React.useContext(TableContext);
  const content =
    typeof children === 'function'
      ? columnKeys.map((key, index) => (
          <React.Fragment key={`${key}-${index}`}>
            {children(key)}
          </React.Fragment>
        ))
      : children;

  return <tr>{content}</tr>;
}

export function TableCell({
  className,
  children,
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={cn('px-3 py-2 align-middle text-slate-700', className)}>
      {children}
    </td>
  );
}

type OverlayContextValue = {
  close: () => void;
};

const OverlayContext = React.createContext<OverlayContextValue>({
  close: () => undefined,
});

function sizeToWidth(size?: string) {
  if (!size || size === 'md') {
    return 'max-w-lg';
  }

  if (size === 'sm') {
    return 'max-w-sm';
  }

  if (size === 'lg') {
    return 'max-w-2xl';
  }

  if (size === 'xl') {
    return 'max-w-3xl';
  }

  if (size === '2xl') {
    return 'max-w-4xl';
  }

  if (size === 'full') {
    return 'max-w-[95vw]';
  }

  return 'max-w-lg';
}

export function Modal({
  isOpen,
  onOpenChange,
  onClose,
  children,
  placement = 'center',
}: {
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  onClose?: () => void;
  children?: React.ReactNode;
  scrollBehavior?: 'inside' | 'outside';
  placement?: 'center' | 'top' | 'bottom';
  size?: string;
  backdrop?: 'opaque' | 'blur' | 'transparent';
}) {
  const close = React.useCallback(() => {
    onOpenChange?.(false);
    onClose?.();
  }, [onClose, onOpenChange]);

  if (!isOpen) {
    return null;
  }

  let alignClass = 'items-center';
  if (placement === 'top') {
    alignClass = 'items-start pt-12';
  } else if (placement === 'bottom') {
    alignClass = 'items-end pb-12';
  }

  return (
    <OverlayContext.Provider value={{ close }}>
      <div
        className={cn(
          'fixed inset-0 z-50 flex justify-center bg-black/40 p-4',
          alignClass,
        )}
      >
        <button
          type='button'
          className='absolute inset-0 cursor-default'
          aria-label='Close'
          onClick={close}
        />
        {children}
      </div>
    </OverlayContext.Provider>
  );
}

export function ModalContent({
  children,
  className,
  size,
}: {
  children?: React.ReactNode | ((onClose: () => void) => React.ReactNode);
  className?: string;
  size?: string;
}) {
  const { close } = React.useContext(OverlayContext);
  return (
    <div
      className={cn(
        'relative z-10 max-h-[90vh] w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl',
        sizeToWidth(size),
        className,
      )}
    >
      {typeof children === 'function' ? children(close) : children}
    </div>
  );
}

export function ModalHeader({
  className,
  children,
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'border-b border-slate-100 px-5 py-4 text-base font-semibold',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function ModalBody({
  className,
  children,
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('space-y-3 px-5 py-4', className)}>{children}</div>;
}

export function ModalFooter({
  className,
  children,
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 px-5 py-3',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function Drawer({
  isOpen,
  onOpenChange,
  onClose,
  children,
  placement = 'right',
  isDismissable = true,
}: {
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  onClose?: () => void;
  children?: React.ReactNode;
  placement?: 'left' | 'right';
  size?: string;
  className?: string;
  hideCloseButton?: boolean;
  isDismissable?: boolean;
}) {
  const close = React.useCallback(() => {
    onOpenChange?.(false);
    onClose?.();
  }, [onClose, onOpenChange]);

  if (!isOpen) {
    return null;
  }

  return (
    <OverlayContext.Provider value={{ close }}>
      <div className='fixed inset-0 z-50 flex bg-black/40'>
        {placement === 'right' ? <div className='flex-1' /> : null}
        {isDismissable ? (
          <button
            type='button'
            className='absolute inset-0 cursor-default'
            aria-label='Close'
            onClick={close}
          />
        ) : null}
        {children}
        {placement === 'left' ? <div className='flex-1' /> : null}
      </div>
    </OverlayContext.Provider>
  );
}

export function DrawerContent({
  className,
  children,
  size = 'md',
}: {
  className?: string;
  children?: React.ReactNode;
  size?: string;
}) {
  let widthClass = 'w-[28rem]';
  if (size === 'full') {
    widthClass = 'w-[95vw]';
  } else if (size === 'sm') {
    widthClass = 'w-[20rem]';
  }

  return (
    <div
      className={cn(
        'relative z-10 h-full max-h-screen overflow-y-auto border-l border-slate-200 bg-white shadow-xl',
        widthClass,
        className,
      )}
    >
      {children}
    </div>
  );
}

export function DrawerHeader({
  className,
  children,
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'border-b border-slate-100 px-4 py-3 text-base font-semibold',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function DrawerBody({
  className,
  children,
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-4 py-3', className)}>{children}</div>;
}

const DROPDOWN_MENU_ATTR = 'data-dropdown-menu';

type MenuContextValue = {
  open: boolean;
  setOpen: (value: boolean) => void;
  onAction?: (key: Key) => void;
  triggerRef: React.RefObject<HTMLDivElement | null>;
};

const MenuContext = React.createContext<MenuContextValue>({
  open: false,
  setOpen: () => undefined,
  triggerRef: { current: null },
});

export function Dropdown({
  children,
  placement,
}: {
  children?: React.ReactNode;
  placement?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideTrigger = triggerRef.current?.contains(target) === true;
      const insideMenu =
        target instanceof Element &&
        target.closest?.(`[${DROPDOWN_MENU_ATTR}]`);
      if (insideTrigger || insideMenu) {
        return;
      }
      setOpen(false);
    };
    window.addEventListener('mousedown', onPointerDown);
    return () => window.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  return (
    <MenuContext.Provider value={{ open, setOpen, triggerRef }}>
      <div
        ref={triggerRef}
        className={cn('relative inline-flex', placement ? '' : '')}
      >
        {children}
      </div>
    </MenuContext.Provider>
  );
}

export function DropdownTrigger({ children }: { children?: React.ReactNode }) {
  const { open, setOpen } = React.useContext(MenuContext);
  if (!React.isValidElement(children)) {
    return <>{children}</>;
  }

  const child = children as React.ReactElement<{
    onClick?: React.MouseEventHandler<HTMLElement>;
  }>;
  return React.cloneElement(child, {
    onClick: (e: React.MouseEvent<HTMLElement>) => {
      child.props.onClick?.(e);
      setOpen(!open);
    },
  });
}

const DropdownMenuContext = React.createContext<{
  onAction?: (key: Key) => void;
}>({});

export function DropdownMenu({
  className,
  children,
  items,
  onAction,
  selectedKeys,
}: {
  className?: string;
  children?: React.ReactNode | ((item: unknown) => React.ReactNode);
  items?: unknown[];
  onAction?: (key: Key) => void;
  selectionMode?: 'single' | 'multiple';
  selectedKeys?: Iterable<Key>;
  itemClasses?: Record<string, string>;
  'aria-label'?: string;
}) {
  const { open, triggerRef } = React.useContext(MenuContext);
  const [menuStyle, setMenuStyle] = React.useState<React.CSSProperties>({});
  React.useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 4;
    const minSpaceBelow = 200;
    const spaceBelow = window.innerHeight - rect.bottom;
    const showAbove = spaceBelow < minSpaceBelow && rect.top > spaceBelow;

    setMenuStyle({
      position: 'fixed',
      ...(showAbove
        ? { bottom: window.innerHeight - rect.top + gap }
        : { top: rect.bottom + gap }),
      right: window.innerWidth - rect.right,
      zIndex: 9999,
    });
  }, [open, triggerRef]);

  if (!open) {
    return null;
  }

  const selected = new Set(toKeyList(selectedKeys).map((k) => String(k)));
  let contentNodes: React.ReactNode[] = [];

  if (Array.isArray(items) && typeof children === 'function') {
    contentNodes = items.map((item, index) => (
      <React.Fragment key={index}>{children(item)}</React.Fragment>
    ));
  } else if (typeof children === 'function') {
    contentNodes = [];
  } else {
    contentNodes = React.Children.toArray(children);
  }

  const menuContent = (
    <DropdownMenuContext.Provider value={{ onAction }}>
      <div
        {...{ [DROPDOWN_MENU_ATTR]: true }}
        className={cn(
          'min-w-40 rounded-md border border-slate-200 bg-white p-1 shadow-lg',
          className,
        )}
        style={menuStyle}
      >
        {contentNodes.map((node, index) => {
          if (!React.isValidElement<DropdownItemProps>(node)) {
            return <div key={index}>{node}</div>;
          }

          const itemKey = cleanKey(node.key, `${index}`);
          return React.cloneElement(node, {
            __itemKey: itemKey,
            __selected: selected.has(itemKey),
          });
        })}
      </div>
    </DropdownMenuContext.Provider>
  );

  return createPortal(menuContent, document.body);
}

type DropdownItemProps = {
  children?: React.ReactNode;
  className?: string;
  onPress?: () => void;
  onClick?: React.MouseEventHandler<HTMLElement>;
  startContent?: React.ReactNode;
  color?: Color;
  isDisabled?: boolean;
  __itemKey?: string;
  __selected?: boolean;
};

export function DropdownItem({
  children,
  className,
  onPress,
  onClick,
  startContent,
  color = 'default',
  isDisabled,
  __itemKey,
  __selected,
}: DropdownItemProps) {
  const { setOpen } = React.useContext(MenuContext);
  const { onAction } = React.useContext(DropdownMenuContext);
  const colorClassName =
    color === 'danger'
      ? 'text-red-600 hover:bg-red-50'
      : 'text-slate-700 hover:bg-slate-100';

  return (
    <button
      type='button'
      disabled={isDisabled}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
        colorClassName,
        __selected ? 'bg-slate-100' : '',
        className,
      )}
      onClick={(e) => {
        onClick?.(e);
        if (!isDisabled) {
          onPress?.();
          if (__itemKey) {
            onAction?.(__itemKey);
          }

          setOpen(false);
        }
      }}
    >
      {startContent}
      {children}
    </button>
  );
}

type PopoverContextValue = {
  open: boolean;
  setOpen: (value: boolean) => void;
  triggerRef: React.RefObject<HTMLDivElement>;
  contentRef: React.RefObject<HTMLDivElement>;
};

const PopoverContext = React.createContext<PopoverContextValue>({
  open: false,
  setOpen: () => undefined,
  triggerRef: { current: null },
  contentRef: { current: null },
});

export function Popover({
  children,
  isOpen,
  onOpenChange,
  wrapperClassName,
}: {
  children?: React.ReactNode;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  placement?: string;
  offset?: number;
  shouldCloseOnScroll?: boolean;
  wrapperClassName?: string;
}) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const controlled = typeof isOpen !== 'undefined';
  const open = controlled ? Boolean(isOpen) : internalOpen;
  const setOpen = React.useCallback(
    (value: boolean) => {
      if (!controlled) {
        setInternalOpen(value);
      }

      onOpenChange?.(value);
    },
    [controlled, onOpenChange],
  );
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = triggerRef.current?.contains(target) ?? false;
      const inContent = contentRef.current?.contains(target) ?? false;
      if (!inTrigger && !inContent) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onPointerDown);
    return () => window.removeEventListener('mousedown', onPointerDown);
  }, [open, setOpen]);

  return (
    <PopoverContext.Provider value={{ open, setOpen, triggerRef, contentRef }}>
      <div
        ref={triggerRef}
        className={cn('relative inline-flex w-full', wrapperClassName)}
      >
        {children}
      </div>
    </PopoverContext.Provider>
  );
}

export function PopoverTrigger({ children }: { children?: React.ReactNode }) {
  const { open, setOpen } = React.useContext(PopoverContext);
  if (!React.isValidElement(children)) {
    return <>{children}</>;
  }

  const child = children as React.ReactElement<{
    onClick?: React.MouseEventHandler<HTMLElement>;
  }>;
  return React.cloneElement(child, {
    onClick: (e: React.MouseEvent<HTMLElement>) => {
      child.props.onClick?.(e);
      setOpen(!open);
    },
  });
}

export function PopoverContent({
  children,
  className,
  portal,
}: {
  children?: React.ReactNode;
  className?: string;
  /** When true, render content in a portal so it is not clipped by overflow. */
  portal?: boolean;
}) {
  const { open, triggerRef, contentRef } = React.useContext(PopoverContext);
  if (!open) {
    return null;
  }

  const baseClassName = cn(
    'rounded-md border border-slate-200 bg-white p-2 shadow-lg',
    className,
  );

  if (portal) {
    const rect = triggerRef.current?.getBoundingClientRect();
    const gap = 4;
    const minPopoverWidth = 280;
    const style: React.CSSProperties = {
      position: 'fixed',
      zIndex: 9999,
    };
    if (rect !== undefined) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const left = rect.left;
      const top = rect.bottom + gap;
      // Keep popover on screen horizontally: prefer right-align when trigger is near right edge
      if (left + minPopoverWidth > viewportWidth) {
        style.right = viewportWidth - rect.right;
        style.left = 'auto';
      } else {
        style.left = Math.max(0, left);
      }
      style.top = Math.min(top, viewportHeight - 80);
    } else {
      style.top = 0;
      style.left = 0;
    }

    return createPortal(
      <div ref={contentRef} className={baseClassName} style={style}>
        {children}
      </div>,
      document.body,
    );
  }

  return (
    <div
      ref={contentRef}
      className={cn('absolute left-0 top-full z-40 mt-1', baseClassName)}
    >
      {children}
    </div>
  );
}

export function Tooltip({
  children,
  content,
  className,
}: {
  children?: React.ReactNode;
  content?: React.ReactNode;
  className?: string;
  offset?: number;
}) {
  const title = typeof content === 'string' ? content : undefined;
  return (
    <span title={title} className={cn('inline-flex', className)}>
      {children}
    </span>
  );
}

type TabsContextValue = {
  active: string;
  setActive: (value: string) => void;
};

const TabsContext = React.createContext<TabsContextValue>({
  active: '',
  setActive: () => undefined,
});

type TabDescriptor = {
  key: string;
  title: React.ReactNode;
  content: React.ReactNode;
};

function normalizeTabs(params: {
  items?: unknown[];
  children?: React.ReactNode | ((item: unknown) => React.ReactNode);
}): TabDescriptor[] {
  const result: TabDescriptor[] = [];
  const { items, children } = params;

  if (Array.isArray(items) && typeof children === 'function') {
    items.forEach((item, index) => {
      const rendered = children(item);
      const arr = React.Children.toArray(rendered);
      arr.forEach((node, subIndex) => {
        if (!React.isValidElement<TabProps>(node)) {
          return;
        }

        result.push({
          key: cleanKey(node.key, `${index}-${subIndex}`),
          title: node.props.title,
          content: node.props.children,
        });
      });
    });

    return result;
  }

  if (typeof children === 'function') {
    return result;
  }

  const arr = React.Children.toArray(children);
  arr.forEach((node, index) => {
    if (!React.isValidElement<TabProps>(node)) {
      return;
    }

    result.push({
      key: cleanKey(node.key, `${index}`),
      title: node.props.title,
      content: node.props.children,
    });
  });

  return result;
}

type TabProps = {
  title?: React.ReactNode;
  children?: React.ReactNode;
};

export function Tabs({
  className,
  items,
  children,
  selectedKey,
  onSelectionChange,
  classNames,
  variant = 'underlined',
  size = 'md',
}: {
  className?: string;
  items?: unknown[];
  children?: React.ReactNode | ((item: unknown) => React.ReactNode);
  selectedKey?: Key;
  onSelectionChange?: (key: Key) => void;
  classNames?: { tabList?: string; tab?: string; content?: string };
  variant?: 'underlined' | 'solid' | 'bordered';
  size?: Size;
  'aria-label'?: string;
}) {
  const tabs = normalizeTabs({ items, children });
  const controlled = typeof selectedKey !== 'undefined';
  const [internal, setInternal] = React.useState(tabs[0]?.key || '');
  const active = controlled ? String(selectedKey) : internal;

  React.useEffect(() => {
    if (!tabs.length) {
      return;
    }

    const exists = tabs.some((tab) => tab.key === active);
    if (!exists && !controlled) {
      setInternal(tabs[0].key);
    }
  }, [active, controlled, tabs]);

  const setActive = (key: string) => {
    if (!controlled) {
      setInternal(key);
    }

    onSelectionChange?.(key);
  };

  const activeTab = tabs.find((tab) => tab.key === active) || tabs[0];
  let tabSize = 'px-4 py-2 text-sm';
  if (size === 'sm') {
    tabSize = 'px-3 py-1.5 text-sm';
  } else if (size === 'lg') {
    tabSize = 'px-5 py-2.5 text-base';
  }

  const activeClass =
    variant === 'underlined'
      ? 'border-b-2 border-blue-600 text-blue-700'
      : 'rounded-md bg-blue-100 text-blue-700';

  return (
    <TabsContext.Provider value={{ active: activeTab?.key || '', setActive }}>
      <div className={cn('w-full', className)}>
        <div
          className={cn(
            'mb-2 flex shrink-0 items-center gap-1 overflow-x-auto border-b border-slate-200',
            classNames?.tabList,
          )}
        >
          {tabs.map((tab) => {
            const isActive = tab.key === activeTab?.key;
            return (
              <button
                key={tab.key}
                type='button'
                className={cn(
                  'whitespace-nowrap font-medium text-slate-600 transition hover:text-slate-900',
                  tabSize,
                  isActive ? activeClass : '',
                  classNames?.tab,
                )}
                onClick={() => setActive(tab.key)}
              >
                {tab.title}
              </button>
            );
          })}
        </div>
        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col overflow-hidden',
            classNames?.content,
          )}
        >
          {activeTab?.content}
        </div>
      </div>
    </TabsContext.Provider>
  );
}

export function Tab(_props: TabProps) {
  return null;
}

export function ScrollShadow({
  className,
  children,
  orientation = 'vertical',
}: {
  className?: string;
  children?: React.ReactNode;
  orientation?: 'horizontal' | 'vertical';
}) {
  const axisClass =
    orientation === 'horizontal' ? 'overflow-x-auto' : 'overflow-y-auto';
  return <div className={cn(axisClass, className)}>{children}</div>;
}

export function Breadcrumbs({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  const arr = React.Children.toArray(children).filter(Boolean);
  return (
    <nav
      className={cn(
        'flex items-center gap-2 text-sm text-slate-600',
        className,
      )}
    >
      {arr.map((child, index) => (
        <React.Fragment key={index}>
          {child}
          {index < arr.length - 1 ? (
            <span className='text-slate-400'>/</span>
          ) : null}
        </React.Fragment>
      ))}
    </nav>
  );
}

export function BreadcrumbItem({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <span className={cn('inline-flex items-center', className)}>
      {children}
    </span>
  );
}

export function Navbar({
  className,
  children,
  maxWidth = 'max-w-screen-2xl',
  height = '4rem',
}: {
  className?: string;
  children?: React.ReactNode;
  maxWidth?: string;
  height?: string;
}) {
  return (
    <header
      className={cn(
        'flex w-full border-b border-slate-200 bg-white px-4 py-0',
        className,
      )}
      style={{ minHeight: height }}
    >
      <div
        className={cn('mx-auto flex w-full items-center gap-4', maxWidth)}
        style={{ minHeight: height }}
      >
        {children}
      </div>
    </header>
  );
}

export function NavbarBrand({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  return <div className={cn('flex items-center', className)}>{children}</div>;
}

export function NavbarContent({
  className,
  children,
  justify,
}: {
  className?: string;
  children?: React.ReactNode;
  justify?: 'start' | 'center' | 'end';
}) {
  let justifyClass = 'justify-start';
  if (justify === 'center') {
    justifyClass = 'justify-center';
  } else if (justify === 'end') {
    justifyClass = 'justify-end';
  }

  return (
    <div className={cn('flex items-center gap-2', justifyClass, className)}>
      {children}
    </div>
  );
}

export function NavbarItem({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  return <div className={cn('flex items-center', className)}>{children}</div>;
}

export function Listbox({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <ul className={cn('flex w-full flex-col gap-1', className)}>{children}</ul>
  );
}

export function ListboxItem({
  className,
  children,
  startContent,
  onPress,
  href,
}: {
  className?: string;
  children?: React.ReactNode;
  startContent?: React.ReactNode;
  onPress?: () => void;
  href?: string;
}) {
  const content = (
    <>
      {startContent}
      <span>{children}</span>
    </>
  );

  if (href) {
    return (
      <li>
        <a
          href={href}
          className={cn(
            'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-white/10',
            className,
          )}
          onClick={(e) => {
            e.preventDefault();
            onPress?.();
          }}
        >
          {content}
        </a>
      </li>
    );
  }

  return (
    <li>
      <button
        type='button'
        className={cn(
          'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-white/10',
          className,
        )}
        onClick={onPress}
      >
        {content}
      </button>
    </li>
  );
}

export function Accordion({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
  variant?: Variant;
}) {
  return <div className={cn('w-full', className)}>{children}</div>;
}

export function AccordionItem({
  className,
  title,
  children,
}: {
  className?: string;
  title?: React.ReactNode;
  children?: React.ReactNode;
  'aria-label'?: string;
}) {
  return (
    <details
      className={cn('rounded-md border border-slate-200 bg-white', className)}
    >
      <summary className='cursor-pointer list-none px-3 py-2 text-sm font-medium text-slate-700'>
        {title}
      </summary>
      <div className='border-t border-slate-100 px-3 py-2'>{children}</div>
    </details>
  );
}

export function Checkbox({
  className,
  children,
  defaultSelected,
  isSelected,
  onValueChange,
  onChange,
}: {
  className?: string;
  children?: React.ReactNode;
  size?: Size;
  defaultSelected?: boolean;
  isSelected?: boolean;
  onValueChange?: (value: boolean) => void;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}) {
  const controlled = typeof isSelected !== 'undefined';
  const [internal, setInternal] = React.useState(Boolean(defaultSelected));
  const checked = controlled ? Boolean(isSelected) : internal;
  return (
    <label
      className={cn(
        'inline-flex items-center gap-2 text-sm text-slate-700',
        className,
      )}
    >
      <input
        type='checkbox'
        checked={checked}
        onChange={(e) => {
          if (!controlled) {
            setInternal(e.target.checked);
          }

          onChange?.(e);
          onValueChange?.(e.target.checked);
        }}
      />
      <span>{children}</span>
    </label>
  );
}

export function Switch({
  className,
  children,
  isSelected,
  onValueChange,
}: {
  className?: string;
  children?: React.ReactNode;
  size?: Size;
  isSelected?: boolean;
  onValueChange?: (value: boolean) => void;
}) {
  const [internal, setInternal] = React.useState(Boolean(isSelected));
  const controlled = typeof isSelected !== 'undefined';
  const checked = controlled ? Boolean(isSelected) : internal;
  return (
    <label
      className={cn(
        'inline-flex items-center gap-2 text-sm text-slate-700',
        className,
      )}
    >
      <button
        type='button'
        className={cn(
          'relative inline-flex h-6 w-10 rounded-full transition',
          checked ? 'bg-blue-600' : 'bg-slate-300',
        )}
        onClick={() => {
          const next = !checked;
          if (!controlled) {
            setInternal(next);
          }

          onValueChange?.(next);
        }}
      >
        <span
          className={cn(
            'absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition',
            checked ? 'translate-x-4' : '',
          )}
        />
      </button>
      {children}
    </label>
  );
}

export function DatePicker({
  className,
  label,
  value,
  onChange,
  isReadOnly,
  isDisabled,
}: {
  className?: string;
  label?: React.ReactNode;
  value?: CalendarDate | null;
  onChange?: (value: CalendarDate | null) => void;
  showMonthAndYearPickers?: boolean;
  isReadOnly?: boolean;
  isDisabled?: boolean;
}) {
  const inputValue = value ? value.toString() : '';
  return (
    <Input
      type='date'
      className={className}
      label={label}
      value={inputValue}
      isReadOnly={isReadOnly}
      isDisabled={isDisabled}
      onValueChange={(next) => {
        if (!next) {
          onChange?.(null);
          return;
        }

        onChange?.(parseDate(next));
      }}
    />
  );
}

export function TimeInput({
  className,
  label,
  value,
  onChange,
  endContent,
}: {
  className?: string;
  label?: React.ReactNode;
  value?: Time | null;
  onChange?: (value: Time | null) => void;
  endContent?: React.ReactNode;
}) {
  const hour = value?.hour ?? 0;
  const minute = value?.minute ?? 0;
  const inputValue =
    value === null || typeof value === 'undefined'
      ? ''
      : `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

  return (
    <Input
      type='time'
      className={className}
      label={label}
      value={inputValue}
      endContent={endContent}
      onValueChange={(next) => {
        if (!next) {
          onChange?.(null);
          return;
        }

        const [h, m] = next.split(':');
        const hh = Number.parseInt(h || '0', 10);
        const mm = Number.parseInt(m || '0', 10);
        onChange?.(new Time(hh, mm));
      }}
    />
  );
}

export { cn };
export { PhoneInput } from './phone-input';
